import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  Copy,
  RefreshCcw,
  Search,
  Filter,
  X,
  CheckCircle2,
  XCircle,
  Trophy,
  Crown,
  User as UserIcon,
  Hash,
  Ticket,
} from "lucide-react";
import Layout from "../../components/Layout";
import { api, formatApiError } from "../../lib/api";
import { formatEuros } from "../../lib/format";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";

const STATUS_LABELS = {
  pending: { label: "En attente", tone: "text-amber-300 border-amber-400/40 bg-amber-400/10" },
  paid: { label: "Payé", tone: "text-emerald-300 border-emerald-400/40 bg-emerald-400/10" },
  rejected: { label: "Refusé", tone: "text-rose-300 border-rose-400/40 bg-rose-400/10" },
};

const REJECT_REASONS = [
  "IBAN invalide",
  "Informations incohérentes",
  "Suspicion de fraude",
  "Demande en doublon",
  "Autre",
];

function copyToClipboard(text, label) {
  if (!text) return;
  navigator.clipboard
    .writeText(text)
    .then(() => toast.success(`${label} copié`))
    .catch(() => toast.error("Impossible de copier"));
}

export default function AdminPrizeClaims() {
  const navigate = useNavigate();
  const [status, setStatus] = useState("pending");
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState("claimed_at_asc");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState({ pending: 0, paid: 0, rejected: 0 });
  const [selected, setSelected] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set("status", status);
      params.set("sort", sort);
      if (search.trim()) params.set("q", search.trim());
      const { data } = await api.get(`/admin/prize-claims?${params.toString()}`);
      setItems(data.items || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const { data } = await api.get("/admin/prize-claims/stats");
      setStats(data || { pending: 0, paid: 0, rejected: 0 });
    } catch (e) {
      /* silent — badge only */
    }
  };

  useEffect(() => {
    load();
    loadStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, sort]);

  const filters = useMemo(
    () => [
      { key: "all", label: "Tous", count: stats.pending + stats.paid + stats.rejected },
      { key: "pending", label: "En attente", count: stats.pending },
      { key: "paid", label: "Payés", count: stats.paid },
      { key: "rejected", label: "Refusés", count: stats.rejected },
    ],
    [stats],
  );

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-4 py-8">
        <button
          data-testid="admin-claims-back"
          onClick={() => navigate("/admin")}
          className="inline-flex items-center gap-2 text-sm text-white/60 hover:text-white transition mb-6"
        >
          <ArrowLeft className="w-4 h-4" /> Retour Admin
        </button>

        <div className="flex items-center justify-between gap-4 flex-wrap mb-8">
          <div>
            <h1 className="font-display text-3xl sm:text-4xl font-black">
              Réclamations de gains
            </h1>
            <p className="text-sm text-white/60 mt-1">
              Traiter les demandes de virement des gagnants.
            </p>
          </div>
          <button
            data-testid="admin-claims-refresh"
            onClick={() => {
              load();
              loadStats();
            }}
            className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-white/10 hover:border-white/30 text-xs uppercase tracking-widest font-mono-stat text-white/70 hover:text-white transition"
          >
            <RefreshCcw className="w-3.5 h-3.5" /> Rafraîchir
          </button>
        </div>

        {/* Filtres */}
        <div className="flex items-center gap-2 flex-wrap mb-4">
          {filters.map((f) => (
            <button
              key={f.key}
              data-testid={`admin-claims-filter-${f.key}`}
              onClick={() => setStatus(f.key)}
              className={`px-3 py-1.5 rounded-full border text-xs uppercase tracking-widest font-mono-stat transition ${
                status === f.key
                  ? "border-cyan-400 text-cyan-200 bg-cyan-400/10"
                  : "border-white/10 text-white/60 hover:border-white/30 hover:text-white"
              }`}
            >
              {f.label} <span className="ml-1 opacity-70">({f.count})</span>
            </button>
          ))}
        </div>

        {/* Search + Sort */}
        <div className="flex items-center gap-3 flex-wrap mb-6">
          <div className="relative flex-1 min-w-[240px] max-w-md">
            <Search className="w-4 h-4 text-white/40 absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              data-testid="admin-claims-search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && load()}
              placeholder="Recherche pseudo / email / code / titulaire / défi"
              className="w-full bg-black/40 border border-white/10 rounded-lg pl-9 pr-3 py-2 text-sm outline-none focus:border-white/30"
            />
          </div>
          <select
            data-testid="admin-claims-sort"
            value={sort}
            onChange={(e) => setSort(e.target.value)}
            className="bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-sm outline-none focus:border-white/30"
          >
            <option value="claimed_at_asc">Plus ancienne d&apos;abord</option>
            <option value="claimed_at_desc">Plus récente d&apos;abord</option>
            <option value="amount_desc">Montant décroissant</option>
            <option value="amount_asc">Montant croissant</option>
          </select>
          <button
            data-testid="admin-claims-search-btn"
            onClick={load}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-white/10 hover:border-white/30 text-xs uppercase tracking-widest font-mono-stat"
          >
            <Filter className="w-3.5 h-3.5" /> Filtrer
          </button>
        </div>

        {/* Tableau */}
        <div className="panel rounded-2xl border border-white/10 overflow-hidden">
          {loading ? (
            <div className="p-8 text-center text-white/50 text-sm">Chargement…</div>
          ) : items.length === 0 ? (
            <div
              data-testid="admin-claims-empty"
              className="p-10 text-center text-white/50 text-sm"
            >
              Aucune réclamation avec ces critères.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-[10px] uppercase tracking-widest font-mono-stat text-white/40 border-b border-white/10">
                  <tr>
                    <th className="text-left px-4 py-3">Date</th>
                    <th className="text-left px-4 py-3">Statut</th>
                    <th className="text-left px-4 py-3">Défi</th>
                    <th className="text-left px-4 py-3">Joueur</th>
                    <th className="text-left px-4 py-3">Rang</th>
                    <th className="text-right px-4 py-3">Montant</th>
                    <th className="text-left px-4 py-3">IBAN</th>
                    <th className="text-right px-4 py-3">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/5">
                  {items.map((c) => (
                    <ClaimRow
                      key={c.session_id}
                      c={c}
                      onOpen={() => setSelected(c.session_id)}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {selected && (
        <ClaimDetailModal
          sessionId={selected}
          onClose={() => setSelected(null)}
          onUpdated={() => {
            load();
            loadStats();
          }}
        />
      )}
    </Layout>
  );
}

function ClaimRow({ c, onOpen }) {
  const s = STATUS_LABELS[c.claim_status] || STATUS_LABELS.pending;
  const isGolden = c.challenge_category === "golden";
  return (
    <tr
      data-testid={`admin-claim-row-${c.session_id}`}
      className="hover:bg-white/5 transition cursor-pointer"
      onClick={onOpen}
    >
      <td className="px-4 py-3 whitespace-nowrap text-white/70 text-xs">
        {c.claimed_at
          ? new Date(c.claimed_at).toLocaleString("fr-FR", {
              day: "2-digit",
              month: "short",
              year: "2-digit",
              hour: "2-digit",
              minute: "2-digit",
            })
          : "—"}
      </td>
      <td className="px-4 py-3">
        <span
          className={`inline-flex px-2 py-0.5 rounded-full border text-[10px] uppercase tracking-widest font-mono-stat ${s.tone}`}
        >
          {s.label}
        </span>
      </td>
      <td className="px-4 py-3">
        <div className="flex items-center gap-1.5">
          {isGolden ? (
            <Crown className="w-3.5 h-3.5 text-amber-300 shrink-0" />
          ) : (
            <Trophy className="w-3.5 h-3.5 text-cyan-300 shrink-0" />
          )}
          <span className="text-white/90 truncate max-w-[200px]">
            {c.challenge_title || "—"}
          </span>
        </div>
      </td>
      <td className="px-4 py-3">
        <div className="text-white/90">{c.nickname || "—"}</div>
        <div className="text-white/40 text-xs">{c.user_email || "—"}</div>
      </td>
      <td className="px-4 py-3 font-mono-stat">
        {c.final_rank ? (
          <>
            #{c.final_rank}
            <span className="text-white/40"> / {c.challenge_winners_count}</span>
          </>
        ) : (
          <span className="text-white/40">—</span>
        )}
      </td>
      <td className="px-4 py-3 text-right font-mono-stat font-bold text-amber-300">
        {c.prize_amount ? formatEuros(c.prize_amount) : <span className="text-white/40">—</span>}
      </td>
      <td className="px-4 py-3 font-mono-stat text-white/70 text-xs">
        {c.iban || "—"}
      </td>
      <td className="px-4 py-3 text-right">
        <button
          data-testid={`admin-claim-detail-${c.session_id}`}
          onClick={(e) => {
            e.stopPropagation();
            onOpen();
          }}
          className="text-xs uppercase tracking-widest font-mono-stat text-cyan-300 hover:text-cyan-200"
        >
          Voir détail
        </button>
      </td>
    </tr>
  );
}

function ClaimDetailModal({ sessionId, onClose, onUpdated }) {
  const [claim, setClaim] = useState(null);
  const [loading, setLoading] = useState(true);
  const [confirmPay, setConfirmPay] = useState(false);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState("IBAN invalide");
  const [rejectCustom, setRejectCustom] = useState("");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/prize-claims/${sessionId}`);
      setClaim(data);
      setNotes(data.admin_notes || "");
    } catch (e) {
      toast.error(formatApiError(e));
      onClose();
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  const pay = async () => {
    setBusy(true);
    try {
      await api.patch(`/admin/prize-claims/${sessionId}/pay`, { admin_notes: notes || null });
      toast.success("Réclamation marquée comme payée");
      setConfirmPay(false);
      await load();
      onUpdated();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const reject = async () => {
    const reason = rejectReason === "Autre" ? rejectCustom.trim() : rejectReason;
    if (!reason || reason.length < 3) {
      toast.error("Merci de préciser une raison (min. 3 caractères)");
      return;
    }
    setBusy(true);
    try {
      await api.patch(`/admin/prize-claims/${sessionId}/reject`, {
        reason,
        admin_notes: notes || null,
      });
      toast.success("Réclamation refusée");
      setRejectOpen(false);
      await load();
      onUpdated();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  if (loading || !claim) {
    return (
      <Dialog open onOpenChange={onClose}>
        <DialogContent data-testid="admin-claim-detail-modal">
          <DialogHeader>
            <DialogTitle>Chargement…</DialogTitle>
          </DialogHeader>
        </DialogContent>
      </Dialog>
    );
  }

  const s = STATUS_LABELS[claim.claim_status] || STATUS_LABELS.pending;
  const isGolden = claim.challenge_category === "golden";
  const canPay = claim.claim_status !== "paid" && claim.claim_status !== "rejected";
  const canReject = claim.claim_status !== "paid" && claim.claim_status !== "rejected";

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent
        data-testid="admin-claim-detail-modal"
        className="max-w-3xl max-h-[90vh] overflow-y-auto"
      >
        <DialogHeader>
          <div className="flex items-center gap-3 flex-wrap">
            <DialogTitle className="text-2xl">
              Réclamation #{sessionId.slice(-6)}
            </DialogTitle>
            <span
              data-testid="admin-claim-detail-status"
              className={`px-2 py-0.5 rounded-full border text-[10px] uppercase tracking-widest font-mono-stat ${s.tone}`}
            >
              {s.label}
            </span>
          </div>
          <DialogDescription>
            Traiter le virement bancaire manuel pour ce gagnant.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {/* Bloc Défi */}
          <Section title="Informations défi" icon={isGolden ? Crown : Trophy}>
            <Field label="Titre" value={claim.challenge_title} testid="detail-challenge-title" />
            <Field label="Type / Catégorie" value={`${claim.challenge_type || "single"} · ${isGolden ? "Golden" : "Standard"}`} />
            <Field label="Fin du défi" value={claim.challenge_ends_at ? new Date(claim.challenge_ends_at).toLocaleString("fr-FR") : "—"} />
            <Field
              label="Cagnotte totale"
              value={formatEuros(claim.challenge_prize_pool_base || 0)}
            />
            <Field
              label="Rang final du joueur"
              value={`#${claim.final_rank} sur ${claim.challenge_winners_count} gagnants`}
            />
            <Field
              label="Montant gagné"
              value={
                <span className="flex items-center gap-2">
                  <span className="font-bold text-amber-300">{formatEuros(claim.prize_amount || 0)}</span>
                  <span className="text-white/50 text-xs">({(claim.prize_percentage || 0).toFixed(0)}%)</span>
                  <CopyBtn value={String(claim.prize_amount || 0)} label="Montant" testid="copy-amount" />
                </span>
              }
              testid="detail-prize-amount"
            />
          </Section>

          {/* Bloc Joueur */}
          <Section title="Informations joueur" icon={UserIcon}>
            <Field label="Pseudo" value={claim.nickname} testid="detail-nickname" />
            <Field label="Email" value={claim.user_email} testid="detail-email" />
            <Field label="User ID" value={<code className="text-xs">{claim.user_id}</code>} />
            <Field label="Date de réclamation" value={claim.claimed_at ? new Date(claim.claimed_at).toLocaleString("fr-FR") : "—"} />
          </Section>

          {/* Bloc Code / session */}
          <Section title="Code & session" icon={Ticket}>
            <Field
              label="Code utilisé"
              value={
                <span className="flex items-center gap-2">
                  <code className="font-mono-stat text-cyan-200">{claim.access_code_value || "—"}</code>
                  <span className="text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded-full border border-white/10 text-white/60">
                    {claim.code_type || "?"}
                  </span>
                </span>
              }
              testid="detail-code"
            />
            <Field label="ID de session" value={<code className="text-xs">{claim.session_id}</code>} />
            <Field label="Score final" value={claim.total_score ?? "—"} />
          </Section>

          {/* Bloc Bancaire */}
          <Section title="Informations bancaires" icon={Hash} tone="ring-1 ring-amber-400/30 bg-amber-400/5">
            <Field
              label="Nom du titulaire"
              value={
                <span className="flex items-center gap-2">
                  <span className="font-bold">{claim.iban_holder_name || "—"}</span>
                  <CopyBtn value={claim.iban_holder_name || ""} label="Titulaire" testid="copy-holder" />
                </span>
              }
              testid="detail-holder"
            />
            <Field
              label="IBAN"
              value={
                <span className="flex items-center gap-2">
                  <code
                    data-testid="detail-iban-full"
                    className="font-mono-stat tracking-wider text-amber-200 select-all"
                  >
                    {claim.iban || "—"}
                  </code>
                  <CopyBtn value={claim.iban || ""} label="IBAN" testid="copy-iban" />
                </span>
              }
            />
            {claim.bic_swift && (
              <Field
                label="BIC / SWIFT"
                value={
                  <span className="flex items-center gap-2">
                    <code className="font-mono-stat">{claim.bic_swift}</code>
                    <CopyBtn value={claim.bic_swift} label="BIC" testid="copy-bic" />
                  </span>
                }
              />
            )}
          </Section>

          {/* Historique admin */}
          {(claim.paid_at || claim.rejected_at) && (
            <Section title="Historique admin">
              {claim.paid_at && (
                <>
                  <Field label="Payé le" value={new Date(claim.paid_at).toLocaleString("fr-FR")} />
                  <Field label="Payé par" value={claim.paid_by || "—"} />
                </>
              )}
              {claim.rejected_at && (
                <>
                  <Field label="Refusé le" value={new Date(claim.rejected_at).toLocaleString("fr-FR")} />
                  <Field label="Refusé par" value={claim.rejected_by || "—"} />
                  <Field label="Raison" value={<span className="text-rose-300">{claim.rejection_reason || "—"}</span>} testid="detail-rejection-reason" />
                </>
              )}
              {claim.admin_notes && <Field label="Note admin" value={claim.admin_notes} />}
            </Section>
          )}

          {/* Notes libres */}
          {canPay && (
            <div>
              <label className="block">
                <span className="text-[10px] uppercase tracking-widest font-mono-stat text-white/50">
                  Note admin (optionnel)
                </span>
                <textarea
                  data-testid="admin-claim-notes"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  rows={2}
                  maxLength={500}
                  className="w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2 mt-1 outline-none focus:border-white/30 text-sm text-white/90"
                  placeholder="Référence virement, remarques internes…"
                />
              </label>
            </div>
          )}
        </div>

        <DialogFooter className="gap-2 flex-wrap">
          <button
            data-testid="admin-claim-close"
            onClick={onClose}
            className="px-4 py-2 rounded-lg border border-white/10 hover:border-white/30 text-xs uppercase tracking-widest font-mono-stat text-white/70"
          >
            Fermer
          </button>
          {canReject && (
            <button
              data-testid="admin-claim-reject-open"
              onClick={() => setRejectOpen(true)}
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg border border-rose-400/40 text-rose-200 hover:bg-rose-400/10 text-xs uppercase tracking-widest font-mono-stat"
            >
              <XCircle className="w-3.5 h-3.5" /> Refuser
            </button>
          )}
          {canPay && (
            <button
              data-testid="admin-claim-pay-open"
              onClick={() => setConfirmPay(true)}
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-emerald-400 text-black hover:bg-emerald-300 text-xs uppercase tracking-widest font-mono-stat font-bold"
            >
              <CheckCircle2 className="w-3.5 h-3.5" /> Marquer comme payé
            </button>
          )}
        </DialogFooter>

        {/* Confirmations */}
        <Dialog open={confirmPay} onOpenChange={setConfirmPay}>
          <DialogContent data-testid="admin-claim-confirm-pay">
            <DialogHeader>
              <DialogTitle>Confirmer le paiement</DialogTitle>
              <DialogDescription>
                Confirmer que le virement de <strong>{formatEuros(claim.prize_amount || 0)}</strong>{" "}
                a bien été effectué vers l&apos;IBAN indiqué ?
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <button
                onClick={() => setConfirmPay(false)}
                className="px-4 py-2 rounded-lg border border-white/10 text-xs uppercase tracking-widest font-mono-stat"
              >
                Annuler
              </button>
              <button
                data-testid="admin-claim-confirm-pay-btn"
                disabled={busy}
                onClick={pay}
                className="px-4 py-2 rounded-lg bg-emerald-400 text-black font-bold text-xs uppercase tracking-widest disabled:opacity-50"
              >
                {busy ? "…" : "Confirmer"}
              </button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <Dialog open={rejectOpen} onOpenChange={setRejectOpen}>
          <DialogContent data-testid="admin-claim-confirm-reject">
            <DialogHeader>
              <DialogTitle>Refuser la réclamation</DialogTitle>
              <DialogDescription>
                Merci d&apos;indiquer la raison du refus. Le joueur pourra la voir dans son espace.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              <label className="block">
                <span className="text-[10px] uppercase tracking-widest font-mono-stat text-white/50">Raison</span>
                <select
                  data-testid="admin-claim-reject-reason"
                  value={rejectReason}
                  onChange={(e) => setRejectReason(e.target.value)}
                  className="w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2 mt-1 outline-none focus:border-white/30 text-sm"
                >
                  {REJECT_REASONS.map((r) => (
                    <option key={r}>{r}</option>
                  ))}
                </select>
              </label>
              {rejectReason === "Autre" && (
                <label className="block">
                  <span className="text-[10px] uppercase tracking-widest font-mono-stat text-white/50">Précision</span>
                  <textarea
                    data-testid="admin-claim-reject-custom"
                    value={rejectCustom}
                    onChange={(e) => setRejectCustom(e.target.value)}
                    rows={3}
                    maxLength={500}
                    className="w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2 mt-1 outline-none focus:border-white/30 text-sm"
                  />
                </label>
              )}
            </div>
            <DialogFooter>
              <button
                onClick={() => setRejectOpen(false)}
                className="px-4 py-2 rounded-lg border border-white/10 text-xs uppercase tracking-widest font-mono-stat"
              >
                Annuler
              </button>
              <button
                data-testid="admin-claim-confirm-reject-btn"
                disabled={busy}
                onClick={reject}
                className="px-4 py-2 rounded-lg bg-rose-400 text-black font-bold text-xs uppercase tracking-widest disabled:opacity-50"
              >
                {busy ? "…" : "Refuser"}
              </button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </DialogContent>
    </Dialog>
  );
}

function Section({ title, icon: Icon, tone = "", children }) {
  return (
    <div className={`rounded-xl border border-white/10 p-4 ${tone}`}>
      <div className="flex items-center gap-2 mb-3">
        {Icon && <Icon className="w-4 h-4 text-cyan-300" />}
        <div className="text-[10px] uppercase tracking-[0.3em] font-mono-stat text-white/60">
          {title}
        </div>
      </div>
      <div className="space-y-2 text-sm">{children}</div>
    </div>
  );
}

function Field({ label, value, testid }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-1 sm:gap-3">
      <div className="text-white/50 text-xs">{label}</div>
      <div className="sm:col-span-2" data-testid={testid}>{value ?? "—"}</div>
    </div>
  );
}

function CopyBtn({ value, label, testid }) {
  if (!value) return null;
  return (
    <button
      data-testid={testid}
      onClick={(e) => {
        e.stopPropagation();
        copyToClipboard(value, label);
      }}
      className="inline-flex items-center gap-1 px-2 py-0.5 rounded border border-white/10 hover:border-white/30 text-[10px] uppercase tracking-widest font-mono-stat text-white/60 hover:text-white transition"
      title={`Copier ${label}`}
    >
      <Copy className="w-3 h-3" /> Copier
    </button>
  );
}
